import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import {
  BUY_BUTTON_STATE,
  canAffordItem,
  CurrencyIcon,
  isSoldOut,
  resolveButtonState,
  resolvePriceDisplay,
  Sticker,
  StoreItemPlaceholder,
} from '@atlas/components';
import {
  CasinoNewProvider,
  NotificationManager,
  SiteConfigManager,
  useApplicationState,
  useCasinoNewState,
} from '@atlas/core';
import { casinoRoutes, COMPONENT_TYPES } from '@atlas/routing';
import {
  Button,
  CASINO_TILE_TYPES,
  CasinoGameTile,
  DefaultPopup,
  Icon,
  Label,
  Tooltip,
} from '@atlas/ui';

import useTranslate from 'common/I18n/Interpreter';
import { GROUPS } from 'components/Casino/constants';
import { buildCasinoGamePath } from 'components/ClientProfile/Bonuses/shared/casinoGameUrl';
import { STORE_TRANSLATION_KEYS } from 'components/Store/translationKeys';

import { openStorePurchaseConfirmationPopup } from '../storePurchaseConfirmationUtils';

import StoreAccordion from './StoreAccordion';
import { openStoreItemDetailsPopup } from './storeItemDetailsUtils';
import StoreSlider from './StoreSlider';

const RelatedItemImage = ({ image, name }) => {
  const [errored, setErrored] = useState(false);

  if (!image || errored) {
    return <StoreItemPlaceholder className="store-item-details__related-placeholder" />;
  }

  return <img alt={name} onError={() => setErrored(true)} src={image} />;
};

RelatedItemImage.propTypes = {
  image: PropTypes.string,
  name: PropTypes.string,
};

const StoreItemDetailsPopupContent = ({ data, id }) => {
  const {
    balances,
    canBuy,
    description,
    discountedPrice,
    hintText,
    history,
    image,
    itemId,
    name,
    pool,
    price,
    purchaseType,
    relatedGames,
    relatedItemIds,
    relatedItemsCatalog,
    ribbon,
  } = data;
  const t = useTranslate();
  const [hasImageError, setHasImageError] = useState(false);
  const imageRef = useRef(null);

  useEffect(() => {
    const imageElement = imageRef.current;

    if (!imageElement) {
      return undefined;
    }

    const handleImageError = () => setHasImageError(true);

    imageElement.addEventListener('error', handleImageError);

    return () => imageElement.removeEventListener('error', handleImageError);
  }, [image]);

  const orderedGames = useMemo(
    () =>
      (relatedGames || [])
        .filter(game => game?.game_public_meta?.enabled)
        .sort(
          (a, b) => (a.game_public_meta?.priority ?? 9999) - (b.game_public_meta?.priority ?? 9999),
        ),
    [relatedGames],
  );

  const relatedItems = useMemo(
    () =>
      (relatedItemIds || [])
        .map(relatedId =>
          (relatedItemsCatalog || []).find(catalogItem => catalogItem.id === relatedId),
        )
        .filter(Boolean),
    [relatedItemIds, relatedItemsCatalog],
  );

  const casinoPath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CASINO, 'casino');

  const handleClose = useCallback(() => {
    NotificationManager.removeNotification(id);
  }, [id]);

  const handlePlayGame = useCallback(
    gameId => {
      if (!gameId || !history) return;

      history.push(buildCasinoGamePath(casinoPath, casinoRoutes.game, gameId));
      handleClose();
    },
    [history, casinoPath, handleClose],
  );

  const { authenticated } = useApplicationState();
  const {
    addToFavorites,
    favoriteGames: { data: favoriteGames },
    identifyFavoriteGames,
    removeFromFavorites,
  } = useCasinoNewState();

  const toggleFavorite = useCallback(
    gameId => {
      if (!authenticated) return;

      if (favoriteGames.includes(gameId)) {
        removeFromFavorites(gameId);
      } else {
        addToFavorites(gameId);
      }
    },
    [authenticated, favoriteGames, addToFavorites, removeFromFavorites],
  );

  useEffect(() => {
    if (!authenticated || !orderedGames.length) return;

    identifyFavoriteGames(orderedGames.map(game => Number(game.ext_game_id)));
  }, [authenticated, orderedGames, identifyFavoriteGames]);

  const priceItem = {
    can_buy: canBuy,
    discounted_price: discountedPrice,
    pool,
    price,
    purchase_type: purchaseType,
  };
  const { hasDiscount, struckPrice, visiblePrice } = resolvePriceDisplay(priceItem);
  const balance = balances?.[purchaseType] ?? null;
  const buttonState = resolveButtonState(canBuy, false, {
    canAfford: canAffordItem(priceItem, balance),
    soldOut: isSoldOut(priceItem),
  });
  const isBuyable =
    buttonState === BUY_BUTTON_STATE.DEFAULT || buttonState === BUY_BUTTON_STATE.HOVER;

  const insufficientFundsMessage = `${t(STORE_TRANSLATION_KEYS.NOT_ENOUGH_COINS)}\n${t(
    STORE_TRANSLATION_KEYS.YOUR_BALANCE,
    { balance: String(balance ?? 0) },
  )}`;

  const handleBuy = useCallback(() => {
    if (!isBuyable) return;

    openStorePurchaseConfirmationPopup({ image, item_id: itemId, name });
    handleClose();
  }, [isBuyable, image, itemId, name, handleClose]);

  const buyLabel = t(STORE_TRANSLATION_KEYS.BUY_FOR);

  const casinoTileType = SiteConfigManager.getConfig(
    'config.config.general.desktopCasinoGameTileView',
    CASINO_TILE_TYPES.TYPE_5,
  );

  const buyButton = (
    <Button
      className="store-item-details__buy"
      disabled={!isBuyable}
      onClick={handleBuy}
      size="big"
      type="primary"
    >
      <span className="store-item-details__buy-content">
        <span className="store-item-details__buy-label">{buyLabel}</span>
        <span className="store-item-details__buy-price">
          {hasDiscount && struckPrice != null && (
            <span className="store-item-details__buy-price-original">{struckPrice}</span>
          )}
          <span className="store-item-details__buy-price-current">{visiblePrice}</span>
        </span>
        <CurrencyIcon purchaseType={purchaseType} />
      </span>
    </Button>
  );

  return (
    <DefaultPopup
      className="store-item-details-popup"
      headerTitlePosition="center"
      onClose={handleClose}
    >
      <Sticker className="store-item-details-popup__ribbon" ribbon={ribbon} size="medium" />

      <div className="store-item-details-popup__scroll">
        <div className="store-item-details-popup__content">
          <h3 className="store-item-details__title">{name}</h3>

          <div className="store-item-details">
            <div className="store-item-details__image">
              {image && !hasImageError ? (
                <img alt={name} ref={imageRef} src={image} />
              ) : (
                <StoreItemPlaceholder />
              )}
            </div>

            {description && <p className="store-item-details__description">{description}</p>}

            {isBuyable ? (
              <div className="store-item-details__buy-wrapper">{buyButton}</div>
            ) : (
              <Tooltip
                className="store-item-details__buy-wrapper"
                message={insufficientFundsMessage}
                placement="top"
              >
                {buyButton}
              </Tooltip>
            )}

            {hintText && (
              <Tooltip
                className="store-item-details__terms"
                message={hintText}
                placement="top"
                trigger="click"
              >
                <Label
                  className="store-item-details__terms-text"
                  message={STORE_TRANSLATION_KEYS.TERMS_CONDITIONS}
                />
                <Icon className="store-item-details__terms-icon" name="info" />
              </Tooltip>
            )}

            {!!relatedItems?.length && (
              <StoreAccordion title={t(STORE_TRANSLATION_KEYS.RELATED_ITEMS)}>
                <StoreSlider>
                  {relatedItems.map(relatedItem => (
                    <StoreSlider.Slide key={relatedItem.id}>
                      <button
                        className="store-item-details__related-card"
                        data-testid="related-item"
                        onClick={() =>
                          openStoreItemDetailsPopup({
                            balances,
                            canBuy: relatedItem.can_buy,
                            description: relatedItem.description,
                            discountedPrice: relatedItem.discounted_price,
                            hintText: relatedItem.hint_text,
                            history,
                            image: relatedItem.image,
                            itemId: relatedItem.id,
                            name: relatedItem.name,
                            pool: relatedItem.pool,
                            price: relatedItem.price,
                            purchaseType: relatedItem.purchase_type,
                            relatedGames: relatedItem.related_games,
                            relatedItemIds: relatedItem.related_item_ids,
                            relatedItemsCatalog,
                            ribbon: relatedItem.ribbon,
                          })
                        }
                        type="button"
                      >
                        <RelatedItemImage image={relatedItem.image} name={relatedItem.name} />
                        <span className="store-item-details__related-name">{relatedItem.name}</span>
                      </button>
                    </StoreSlider.Slide>
                  ))}
                </StoreSlider>
              </StoreAccordion>
            )}

            {!!orderedGames.length && (
              <StoreAccordion title={t(STORE_TRANSLATION_KEYS.RELATED_GAMES)}>
                <StoreSlider>
                  {orderedGames.map(game => {
                    const meta = game.game_public_meta || {};
                    const gameId = Number(game.ext_game_id);
                    const isFavorite = favoriteGames.includes(gameId);

                    return (
                      <StoreSlider.Slide key={game.ext_game_id}>
                        <CasinoGameTile
                          favorite={{
                            onClick: () => toggleFavorite(gameId),
                            state: isFavorite,
                          }}
                          image={meta.image}
                          name={meta.name}
                          onClickPlay={() => handlePlayGame(game.ext_game_id)}
                          provider={meta.game_provider}
                          size="S"
                          type={casinoTileType}
                        />
                      </StoreSlider.Slide>
                    );
                  })}
                </StoreSlider>
              </StoreAccordion>
            )}
          </div>
        </div>
      </div>
    </DefaultPopup>
  );
};

StoreItemDetailsPopupContent.propTypes = {
  data: PropTypes.shape({
    balances: PropTypes.object,
    canBuy: PropTypes.bool,
    description: PropTypes.string,
    discountedPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    hintText: PropTypes.string,
    history: PropTypes.object,
    image: PropTypes.string,
    itemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string.isRequired,
    pool: PropTypes.number,
    price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    purchaseType: PropTypes.oneOf(['points', 'gems', 'diamonds']),
    relatedGames: PropTypes.array,
    relatedItemIds: PropTypes.array,
    relatedItemsCatalog: PropTypes.array,
  }).isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

const StoreItemDetailsPopup = props => (
  <CasinoNewProvider group={GROUPS.CASINO} skipDataLoad>
    <StoreItemDetailsPopupContent {...props} />
  </CasinoNewProvider>
);

export default StoreItemDetailsPopup;
